import { observer } from 'mobx-react-lite';
import { moreDetails as moreDetails_1 } from './QuickCompose.locstring.json';
import { discard } from 'owa-locstrings/lib/strings/discard.locstring.json';
import { sendButtonLabel } from 'owa-locstrings/lib/strings/sendbuttonlabel.locstring.json';
import { confirmDialogSaveText } from 'owa-locstrings/lib/strings/confirmdialogsavetext.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import CelebrationImage from 'owa-calendar-forms-common/lib/components/CelebrationImage';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';

import ComposeActionButton from './ComposeActionButton';
import getComposeStyles from '../utils/getComposeStyles';
import getQuickComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getQuickComposeViewState';
import isCelebrationEvent from '../utils/isCelebrationEvent';
import isQuickComposeInEditMode from 'owa-calendar-compose-form-store/lib/selectors/isQuickComposeInEditMode';
import QuickComposeHeader from './QuickComposeHeader';

import { useGlobalHotkey } from 'owa-hotkeys';
import { getEffectiveCharm } from 'owa-calendar-charms-store';
import {
    CharmSelectionType,
    QuickComposePivotType,
    FULL_COMPOSE_VIEW_TYPE_MODAL,
} from 'owa-calendar-helpers-types/lib/ComposeTypes';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { getCommands } from 'owa-calendar-hotkeys';
import { getComposeCalendarColorScheme } from 'owa-calendar-compose-form-store/lib/selectors/getComposeCalendarColorScheme';
import { lazyShowFullCompose } from 'owa-calendar-compose-form-store';
import { Link } from '@fluentui/react/lib/Link';
import {
    FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE,
    QuickComposeToFullComposeEntryType,
} from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import {
    logQuickComposeToFullComposeEntryType,
    discardQuickComposeEvent,
    verifyEventThenSave,
    prepareEvent,
} from 'owa-calendar-compose-form-actions';
import { newOofCalendarEvent } from '../actions/newOofCalendarEvent';
import { isCssVariablesFeatureEnabled } from 'owa-calendar-feature-flags';
import { isEventAMeeting } from 'owa-calendar-event-capabilities';
import { ComposeFormStore } from 'owa-calendar-compose-types';
import QuickComposeContent from './QuickComposeContent';
import EventContent from './EventContent';
import QuickComposePivots from './QuickComposePivots';
import { QuickComposeOofActionStatus } from 'owa-calendar-helpers-types/lib/QuickComposeOofActionStatus';
import { default as logOofMetrics, FormCloseType } from '../utils/logOofMetrics';
import isOofPivotActive from '../utils/isOofPivotActive';
import shouldShowPivots from 'owa-calendar-compose-form-store/lib/quickCompose/utils/shouldShowPivots';
import 'owa-calendar-compose-form-orchestration';
import '../mutators/quickComposeViewStateMutators';
import { isConsumer } from 'owa-session-store';
import { isFeatureEnabled } from 'owa-feature-flags';

const styles = require('./QuickCompose.scss');
const classNames = require('classnames');

/**
 * Props for quick compose
 */
export interface QuickComposeProps {
    /**
     * Quick Compose state
     */
    state: ComposeFormStore;

    /**
     * Callback for when a key is pressed on quick compose
     */
    onKeyDown?: React.KeyboardEventHandler<EventTarget>;

    /**
     * Callback when for when the component's height increases to rerender the container
     * to ensure it does not go off screen.
     * Pass null if you think your scenario does not need to resize the component height
     */
    onSizeUpdate: () => void;

    /**
     * Optional callback to override the behavior for triggering full compose
     */
    onShowFullCompose?: () => void;
}

/**
 * Component for rendering quick compose
 */
export default observer(function QuickCompose(props: QuickComposeProps) {
    const container = React.useRef<HTMLDivElement>();
    const lastHeight = React.useRef<number>();

    React.useEffect(() => {
        if (container.current) {
            lastHeight.current = container.current.getBoundingClientRect().height;
        }
    }, []);

    useGlobalHotkey(getCommands().saveAppointment, verifyEventThenSave, {
        allowHotkeyOnTextFields: true,
    });

    const updateContainerSize = () => {
        if (!props.onSizeUpdate) {
            // if onSizeUpdate is not passed, then do nothing.
            return;
        }
        if (container.current) {
            const currentHeight = container.current.getBoundingClientRect().height;
            if (currentHeight > lastHeight.current) {
                props.onSizeUpdate();
            }
            lastHeight.current = currentHeight;
        }
    };

    React.useEffect(updateContainerSize);

    const onMouseDown = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        // prevent mouseDown event from propagating outside the peek content so it's not interpreted as a drag
        e.stopPropagation();
    }, []);

    const { state, onShowFullCompose } = props;
    let storeViewState = getQuickComposeViewState();
    // Check prop.state.event before going any further.
    // Even though we check in QuickComposeRenderer that viewState.showQuickCompose is true,
    // our component's render could still be invoked with event and originalEvent being null.
    // This would typically happen right after a save, when state.event props are updated with
    // finalized fix ups (e.g. setFriendlyToFinalizedDate).
    //
    // With react 16 we can also be called when storeViewState is torn down.
    if (!state.event || !storeViewState) {
        return <div />;
    }
    const isInEditMode = isQuickComposeInEditMode();
    const colorScheme: ColorScheme = getComposeCalendarColorScheme();
    const charm = getEffectiveCharm(state.event.ParentFolderId, state.event.CharmId);
    // This function is to support cal-css-variables feature flag and generate the expected style
    const composeGeneratedStyles = getComposeStyles(
        !isCssVariablesFeatureEnabled(),
        colorScheme,
        styles.useCssVariables,
        styles.editTitleTextInputCssVariables
    );
    const commitButtonLabel: string = isEventAMeeting(state.event)
        ? loc(sendButtonLabel)
        : loc(confirmDialogSaveText);
    const viewType = storeViewState.viewType;
    const showPivots: boolean =
        shouldShowPivots(isInEditMode) &&
        viewType != 'TimePanel'; /* We don't want to show pivots in case of timepanel */
    const showMergedEvent: boolean = !isConsumer() && !isInEditMode;
    let disableActionButtons: boolean = false;
    if (storeViewState.activePivot === QuickComposePivotType.OOF && storeViewState.oofViewState) {
        const { mailboxAutoReplyConfigurationLoadingStatus } = storeViewState.oofViewState;
        disableActionButtons =
            mailboxAutoReplyConfigurationLoadingStatus === QuickComposeOofActionStatus.Pending ||
            mailboxAutoReplyConfigurationLoadingStatus === QuickComposeOofActionStatus.Failed;
    }
    return (
        // tabIndex = -1 ensures that the form takes focus on click (even on blank area) but doesn't impact the tab order
        <div
            style={composeGeneratedStyles.quickComposeStyle}
            ref={container}
            className={isInEditMode ? styles.quickEdit : styles.quickCompose}
            onKeyDown={props.onKeyDown}
            tabIndex={-1}>
            {viewType != 'TimePanel' && (
                <QuickComposeHeader
                    className={
                        isFeatureEnabled('cal-surface-dragPeek')
                            ? styles.calendarItemBar
                            : undefined
                    }
                    event={state.event}
                    colorScheme={colorScheme}
                    openFullCompose={onMoreOptionsButtonClicked(
                        QuickComposeToFullComposeEntryType.IconButton,
                        onShowFullCompose
                    )}
                />
            )}
            <div className={styles.content} onMouseDown={onMouseDown}>
                {viewType != 'TimePanel' && isCelebrationEvent(charm.IconId) && (
                    <CelebrationImage
                        event={state.event}
                        colorScheme={colorScheme}
                        charmId={charm.IconId}
                        className={
                            isInEditMode
                                ? classNames(styles.celebrationImage, styles.headerContainer)
                                : styles.watermarkCelebrationImage
                        }
                    />
                )}
                {showPivots ? (
                    <QuickComposePivots
                        state={state}
                        colorScheme={colorScheme}
                        onSizeUpdate={updateContainerSize}
                    />
                ) : showMergedEvent ? (
                    <EventContent
                        event={state.event}
                        colorScheme={colorScheme}
                        onShowFullCompose={onShowFullCompose}
                    />
                ) : (
                    <QuickComposeContent
                        state={state}
                        colorScheme={colorScheme}
                        onSizeUpdate={updateContainerSize}
                        onShowFullCompose={onShowFullCompose}
                    />
                )}

                <div className={styles.footer} style={composeGeneratedStyles.footerStyle}>
                    <ComposeActionButton
                        colors={composeGeneratedStyles.composeActionButtonColors}
                        id={commitButtonLabel}
                        className={classNames(
                            styles.saveButton,
                            composeGeneratedStyles.cssVariabelsStyle
                        )}
                        caption={commitButtonLabel}
                        onClick={saveButtonClicked}
                        disabled={disableActionButtons}
                    />
                    <DefaultButton
                        styles={composeGeneratedStyles.discardButtonStyle}
                        className={classNames(
                            styles.discardButton,
                            composeGeneratedStyles.cssVariabelsStyle
                        )}
                        text={loc(discard)}
                        onClick={discardButtonClicked}
                        disabled={disableActionButtons}
                    />
                    {viewType != 'TimePanel' && (
                        <Link
                            styles={composeGeneratedStyles.linkGetStyles}
                            onClick={onMoreOptionsButtonClicked(
                                QuickComposeToFullComposeEntryType.MoreOptionsButton,
                                onShowFullCompose
                            )}
                            className={classNames(
                                styles.moreDetails,
                                composeGeneratedStyles.cssVariabelsStyle
                            )}>
                            {loc(moreDetails_1)}
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
});

/**
 * Event handler when more details link is clicked to open full compose
 */
function openFullCompose(onShowFullCompose?: () => void) {
    const storeViewState = getQuickComposeViewState();
    if (storeViewState.activePivot === QuickComposePivotType.Event) {
        prepareEvent();
    }
    if (onShowFullCompose) {
        onShowFullCompose();
    } else {
        lazyShowFullCompose
            .importAndExecute(
                storeViewState.charmSelection == CharmSelectionType.Manual,
                FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE,
                FULL_COMPOSE_VIEW_TYPE_MODAL
            )
            .then(() => {
                /* WI 102453. We will re add this code once the OCS setting is ready.
                    if (
                        isFluidEnabledForSource(FluidOwaSource.CalendarCompose) &&
                        hasAttendees(event) &&
                        !event.CollabSpace
                    ) {
                        lazyCreateFluidCollabSpace.importAndExecute(
                            event,
                            FluidOwaSource.CalendarCompose,
                            null
                        );
                    }
                */
            });
    }
}

/**
 * Event handler when save button is clicked.
 * Makes some validations before calling save
 */
function saveButtonClicked() {
    if (isOofPivotActive()) {
        newOofCalendarEvent();
    } else {
        verifyEventThenSave();
    }
}

function discardButtonClicked() {
    if (isOofPivotActive()) {
        logOofMetrics(FormCloseType.Discard);
    }
    discardQuickComposeEvent();
}

function onMoreOptionsButtonClicked(
    entryType: QuickComposeToFullComposeEntryType,
    onShowFullCompose?: () => void
) {
    return () => {
        const activePivot = getQuickComposeViewState().activePivot;
        if (activePivot === QuickComposePivotType.OOF) {
            logOofMetrics(FormCloseType.OptionsPage);
            window.alert('OOF events only saveable from quick-compose');
        } else {
            logQuickComposeToFullComposeEntryType(entryType);
            openFullCompose(onShowFullCompose);
        }
    };
}
